/**
 * TutorOfferScreen — une offre de cours ouverte par son lien : `group-attribution-proposals`
 * (`group_attribution_proposal_id` ou `hash`) et `substitution-proposals` (`substitution_proposal_id`
 * ou `hash`). La même page pour les deux, chacune sous son nom de route.
 *
 * Un fil : la lecture (`useTutorOfferData`), le view-model, les gestes et les réponses
 * (`useTutorOfferState`), la page, et la boîte d'un masquage refusé.
 */
import React from "react"
import { useSelector } from "react-redux"

import { TutorOfferPage } from "../../../../../../design-system/tutor"
import {
    type TutorOfferLink,
    type TutorOfferLinkKind,
    tutorOfferViewModel,
} from "../../../../view-models/tutor/Availability/tutorOfferViewModel"
import { type TutorSpaceScreenProps } from "../tutorSpaceRouteTypes"
import { TutorSendErrorDialog } from "./TutorSendErrorDialog"
import { useTutorOfferData } from "./useTutorOfferData"
import { useTutorOfferState } from "./useTutorOfferState"

const ID_PARAM: Record<TutorOfferLinkKind, string> = {
    group: "group_attribution_proposal_id",
    substitution: "substitution_proposal_id",
}

function TutorOfferScreen({ route, kind }: TutorSpaceScreenProps & { kind: TutorOfferLinkKind }) {
    const link: TutorOfferLink = {
        kind,
        hash: route?.params?.hash ?? null,
        id: route?.params?.[ID_PARAM[kind]] ?? null,
    }
    const { raw, loading, reloadList } = useTutorOfferData(link)
    const offer = useSelector((state: unknown) => tutorOfferViewModel(state, link, raw))
    const { errorMessage, onDismissError, ...gestures } = useTutorOfferState(offer, link, reloadList)

    return (
        <>
            <TutorOfferPage offer={offer} loading={loading && offer == null} {...gestures} testID="tutor-offer" />
            <TutorSendErrorDialog message={errorMessage} onDismiss={onDismissError} />
        </>
    )
}

export function TutorGroupOfferScreen(props: TutorSpaceScreenProps) {
    return <TutorOfferScreen {...props} kind="group" />
}

export function TutorSubstitutionOfferScreen(props: TutorSpaceScreenProps) {
    return <TutorOfferScreen {...props} kind="substitution" />
}
